<script setup>
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import {
  assoc, assocPath, isEmpty, omit, pipe,
} from 'ramda';
import { toValue } from 'vue';
import AccountFormField from '@/components/forms/AccountFormField.vue';
import useCallEndPointWith from '@/services/backend/useCallEndPointWith';
import InPlaceAlert from '@/components/InPlaceAlert.vue';
import AccountFormFieldSelect from '@/components/forms/AccountFormFieldSelect.vue';
import {
  genderOptions,
  countryOptions,
} from '@/services/formUtilities';
import { useCreateSecureRemotePassword, useUserStateFromEndpoint } from '@/services/authentication';
import { registerSpec } from '@/services/register';
import WithLoader from '@/components/WithLoader.vue';
import passwordValidator from '@/services/passwordValidator/passwordValidator';
import getValidationMessages from '@/services/passwordValidator/validationMessages';
import { useGlobalState } from '@/store/user.store.js';
import { trackEvent, updateGtagConsent } from '@/services/analytics/googleAnalytics.js';
import AccountFormFieldCheckbox from '@/components/forms/AccountFormFieldCheckbox.vue';

const { t } = useI18n();

const validationMessages = getValidationMessages();

const emit = defineEmits(['detailsConfirmed', 'back']);

const { defineField, handleSubmit } = useForm({
  validationSchema: yup.object({
    user: yup.object({
      email: yup
        .string()
        .email(t('passwordValidator.emailValid'))
        .lowercase()
        .required(t('passwordValidator.emailRequired'))
        .label(t('registerView.accountForm.labels.email')),
      confirmEmail: yup
        .string()
        .email(t('passwordValidator.emailValid'))
        .lowercase()
        .required(t('passwordValidator.emailRequired'))
        .oneOf([yup.ref('email')], t('passwordValidator.emailMatch'))
        .label(t('registerView.accountForm.labels.confirmEmail')),
      password: passwordValidator(validationMessages).label(
        t('registerView.accountForm.labels.password'),
      ),
      confirmPassword: yup
        .string()
        .required(t('passwordValidator.passwordRequired'))
        .oneOf([yup.ref('password')], t('passwordValidator.passwordMatch'))
        .label(t('registerView.accountForm.labels.confirmPassword')),
      phoneNumber: yup.string().label(t('registerView.accountForm.labels.phoneNumber')),
      gender: yup.string().required().label(t('registerView.accountForm.labels.gender')),
      firstName: yup.string().required().label(t('registerView.accountForm.labels.firstName')),
      lastName: yup.string().required().label(t('registerView.accountForm.labels.lastName')),
      situation: yup.string().label(t('registerView.accountForm.labels.situation')),
      job: yup
        .string()
        .when('situation', {
          is: 'employed',
          then: (schema) => schema,
        })
        .label(t('registerView.accountForm.labels.job')),
      streetAddress: yup
        .string()
        .label(t('registerView.accountForm.labels.streetAddress')),
      zipCode: yup.string().label(t('registerView.accountForm.labels.zipCode')),
      country: yup.string().required().label(t('registerView.accountForm.labels.country')),
      city: yup.string().label(t('registerView.accountForm.labels.city')),
      termsAccepted: yup
        .boolean()
        .oneOf([true], t('registerView.accountForm.labels.termsRequired')).label(t('registerView.accountForm.labels.terms')),
      privacyAccepted: yup
        .boolean()
        .oneOf([true], t('registerView.accountForm.labels.privacyRequired')).label(t('registerView.accountForm.labels.privacy')),
    }),
  }),
});

const [email, emailAttrs] = defineField('user.email');
const [confirmEmail, confirmEmailAttrs] = defineField('user.confirmEmail');
const [password, passwordAttrs] = defineField('user.password');
const [confirmPassword, confirmPasswordAttrs] = defineField('user.confirmPassword');
const [country, countryAttrs] = defineField('user.country');
const [gender, genderAttrs] = defineField('user.gender');
const [firstName, firstNameAttrs] = defineField('user.firstName');
const [lastName, lastNameAttrs] = defineField('user.lastName');
const [termsAccepted, termsAcceptedAttrs] = defineField('user.termsAccepted');
const [privacyAccepted, privacyAcceptedAttrs] = defineField('user.privacyAccepted');

// const [situation, situationAttrs] = defineField('user.situation');
// const [job, jobAttrs] = defineField('user.job');
// const [city, cityAttrs] = defineField('user.city');
// const [phoneNumber, phoneNumberAttrs] = defineField('user.phoneNumber');
// const [streetAddress, streetAddressAttrs] = defineField('user.streetAddress');
// const [zipCode, zipCodeAttrs] = defineField('user.zipCode');

country.value = 'de';

const {
  callEndpoint,
  userFriendlyError,
  isLoading: isSendingRegisterForm,
} = useCallEndPointWith('register');

const { userState, currentEmployees, currentCourses } = useGlobalState();
const { getUserStateFromEndpoint, isLoading } = useUserStateFromEndpoint();

const submit = handleSubmit(async (values) => {
  try {
    const username = values.user.email;
    const { password } = values.user;
    const usernameLowercased = username.toLowerCase();

    const { salt, verifier } = useCreateSecureRemotePassword(usernameLowercased, password);

    const request = pipe(
      assocPath(['user', 'salt'], toValue(salt)),
      assocPath(['user', 'verifier'], toValue(verifier)),
      registerSpec,
      omit(['company']),
    )(values);

    await callEndpoint(assoc('method', 'POST', { request }));
    updateGtagConsent({ 'analytics_storage': 'granted' });
    updateGtagConsent({ 'ad_storage': 'granted' });
    updateGtagConsent({ 'ad_user_data': 'granted' });
    updateGtagConsent({ 'ad_personalization': 'granted' });
    localStorage.setItem('gaRegisteredUserConsent', 'true');
    if (isEmpty(userFriendlyError.value)) {
      const userStateFromEndpoint = await getUserStateFromEndpoint(usernameLowercased, values.user.password);// eslint-disable-line max-len

      if (!isEmpty(userStateFromEndpoint)) {
        userState.value = assoc('isAuthenticated', true, userStateFromEndpoint);
        currentEmployees.value = userStateFromEndpoint.employees;
        currentCourses.set(userStateFromEndpoint.courses);
      }
      trackEvent('individual_registration_success', {
        event_category: 'Register',
        event_label: 'New User',
      });
      emit('detailsConfirmed', values);
    }
  } catch (error) {
    trackEvent('individual_registration_exception', {
      event_category: 'Register',
      event_label: 'Error registering user',
      error: error.message || 'unknown',
    });
    if (error.response && error.response.data && error.response.data.error) {
      userFriendlyError.value = error.response.data.error;
    } else {
      userFriendlyError.value = t('registerView.accountForm.errors.userExists');
    }
  }
});
</script>

<template>
  <div>
    <h2 class="text-3xl text-center text-basecolor mb-8">
      {{ $t('registerView.accountForm.title') }}
    </h2>

    <form
      @submit="submit"
      class="m-10"
    >
      <div class="max-w-4xl mx-auto px-16 py-8 mt-8 base-gradient text-primary rounded-lg">
        <h2 class="text-2xl">
          {{ $t('registerView.accountForm.individualDetailsTitle') }}
        </h2>

        <InPlaceAlert
          :message="userFriendlyError"
          type="error"
          class="mt-3 col-span-2"
          @close-alert="userFriendlyError = ''"
        />

        <div class="px-4 pb-36 pt-8 sm:px-6 lg:col-start-1 lg:row-start-1 lg:px-0 lg:pb-16">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <div class="grid grid-cols-2 md:grid-cols-3 gap-x-3">
              <AccountFormFieldSelect
                class="md:col-span-1"
                id="user.gender"
                :label="$t('registerView.accountForm.labels.gender')"
                :is-required="true"
                v-model="gender"
                v-bind="genderAttrs"
                auto-complete-type="gender"
                :options="genderOptions"
              />
              <AccountFormField
                class="md:col-span-2"
                id="user.firstName"
                :label="$t('registerView.accountForm.labels.firstName')"
                :is-required="true"
                v-model="firstName"
                input-type="text"
                v-bind="firstNameAttrs"
                auto-complete-type="firstname"
              />
            </div>
            <AccountFormField
              id="user.lastName"
              :label="$t('registerView.accountForm.labels.lastName')"
              :is-required="true"
              v-model="lastName"
              input-type="text"
              v-bind="lastNameAttrs"
              auto-complete-type="lastname"
            />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <AccountFormFieldSelect
              id="user.country"
              :label="$t('registerView.accountForm.labels.country')"
              :is-required="true"
              v-model="country"
              v-bind="countryAttrs"
              :options="countryOptions"
            />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <AccountFormField
              id="user.email"
              :label="$t('registerView.accountForm.labels.email')"
              :is-required="true"
              v-model="email"
              v-bind="emailAttrs"
              input-type="email"
              auto-complete-type="email"
            />
            <AccountFormField
              id="user.confirmEmail"
              :label="$t('registerView.accountForm.labels.confirmEmail')"
              :is-required="true"
              v-model="confirmEmail"
              v-bind="confirmEmailAttrs"
              input-type="email"
              auto-complete-type="confirm-email"
            />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <AccountFormField
              id="user.password"
              :label="$t('registerView.accountForm.labels.password')"
              :is-required="true"
              v-model="password"
              v-bind="passwordAttrs"
              input-type="password"
              auto-complete-type="password"
            />
            <AccountFormField
              id="user.confirmPassword"
              :label="$t('registerView.accountForm.labels.confirmPassword')"
              :is-required="true"
              v-model="confirmPassword"
              v-bind="confirmPasswordAttrs"
              input-type="password"
              auto-complete-type="confirm-password"
            />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <AccountFormFieldCheckbox
              id="user.termsAccepted"
              :label="$t('registerView.accountForm.labels.terms')"
              :is-required="true"
              v-model="termsAccepted"
              v-bind="termsAcceptedAttrs"
            />
            <AccountFormFieldCheckbox
              id="user.privacyAccepted"
              :label="$t('registerView.accountForm.labels.privacy')"
              :is-required="true"
              v-model="privacyAccepted"
              v-bind="privacyAcceptedAttrs"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end max-w-4xl mx-auto mt-8">
        <WithLoader :is-loading="isSendingRegisterForm || isLoading">
          <button
            type="submit"
            class="w-full mt-4 px-14 py-2 btn-cta sm:order-last sm:w-auto"
          >
            {{ $t('registerView.accountForm.confirm') }}
          </button>
        </WithLoader>
      </div>
    </form>
  </div>
</template>
